import { set as vueSet } from "vue";
import { LOCAL_STORAGE_KEYS } from "@consts";
import { isEmpty } from "@utils";

export const state = () => ({
	pyramidOfWords: [],
	// True once the player has finished the pirmyd — either by guessing the
	// last level or by running out of tries on it. What actually happened is
	// derived separately, per level, from input/triedOutWords (see
	// common/utils/pirmyd/share.js's getLevelSolvedFlags) rather than tracked
	// as its own flag here — this only needs to answer "is the game over".
	pyramidSolved: false,
	showClues: true,
	showRules: true,
	// Map of levelIndex -> true once the player has seen that level's clue at least once.
	// A level stays in "easy" mode for the rest of the day once its clue has been revealed,
	// even if clues are later switched off.
	cluesSeenByLevel: {},
	// True when FETCH_PYRAMID_OF_DATE couldn't load a puzzle from the pirmyd API.
	loadError: false,
	currentDate: null,
	dayNumber: null,
	// Whether the puzzle currently in pyramidOfWords was fetched for a former
	// date (/play/pirmyd/:date/) or for today. PAGES.PIRMYD and PIRMYD_PAST are
	// different named routes, so navigating between "today" and "a former
	// date" always remounts pages/Pirmyd/Pirmyd.js. The Vuex state (this
	// included) survives that remount, so this is how the fresh instance can
	// tell, before its own fetch resolves, that what's currently loaded
	// belongs to the puzzle it's leaving rather than the one it's opening
	loadedAsFormerDate: false,
});

export const mutations = {
	setPyramid(state, payload) {
		vueSet(state, "pyramidOfWords", payload);
	},
	updatePyramidSolved(state, payload) {
		vueSet(state, "pyramidSolved", payload);
	},
	updateShowRules(state, payload) {
		if (process.client) {
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.PRMD_SHOW_RULES,
				JSON.stringify(payload)
			);
		}
		vueSet(state, "showRules", payload);
	},
	updateShowClues(state, payload) {
		if (process.client) {
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.PRMD_SHOW_CLUES,
				JSON.stringify(payload)
			);
		}
		vueSet(state, "showClues", payload);
	},
	markClueSeenForLevel(state, levelIndex) {
		vueSet(state.cluesSeenByLevel, levelIndex, true);
	},
	updateCluesSeenByLevel(state, payload) {
		vueSet(state, "cluesSeenByLevel", payload || {});
	},
	updateLoadError(state, payload) {
		vueSet(state, "loadError", payload);
	},
	updatePuzzleMeta(state, { date, dayNumber, isFormerDate } = {}) {
		vueSet(state, "currentDate", date ?? null);
		vueSet(
			state,
			"dayNumber",
			typeof dayNumber === "number" ? dayNumber : null
		);
		vueSet(state, "loadedAsFormerDate", Boolean(isFormerDate));
	},
};

export const actions = {
	// Switches the global clue visibility and, when turning clues on, permanently
	// marks the level the player is currently on as having had its clue seen.
	UPDATE_SHOW_CLUES({ commit, rootState }, showClues) {
		commit("updateShowClues", showClues);

		if (showClues) {
			commit("markClueSeenForLevel", rootState.level.currentLevelIndex);
		}
	},
	/*
		Loads a pirmyd puzzle from the API.

		- If `payload.date` ("YYYY-MM-DD") is given, that exact date is requested.
		  Callers use this to ask for "my own local today" (see Pirmyd.js, which
		  always passes its own dayjs().format("YYYY-MM-DD") here once it's running
		  client-side, rather than trusting whatever date the server guessed).
		- Otherwise, if the route is a former puzzle (/play/pirmyd/:date/), that
		  date param is used.
		- Otherwise, no date is sent at all, and the API decides "today" using its
		  own clock (America/New_York). This last case is ONLY ever hit during the
		  very first SSR render, before we know the visitor's timezone — it's just a
		  best-effort guess for the initial paint; the client corrects it for its own
		  timezone immediately after mount (see Pirmyd.js's loadData/mounted).

		IMPORTANT: the upstream API refuses to serve a date later than its own
		"last published" date (America/New_York) — it returns 400 "Invalid date."
		rather than leaking a not-yet-published puzzle. That happens for real for
		this "my own local today" case: a visitor whose local calendar date has
		already rolled over to tomorrow relative to the server (e.g. Australia)
		will have that date rejected as "in the future". When that happens, we
		gracefully fall back to whatever the server considers its latest puzzle
		instead of surfacing an error — the visitor just keeps seeing the current
		puzzle a little longer, until the server's own day catches up.

		Returns { words, date }, where words is an array shaped like the old
		hardcoded PYRAMIDS entries: [{ word, pos, definition }, ...] — index 0 is
		the starting (given) word, indexes 1..n are the levels the player actually
		solves — and date ("YYYY-MM-DD") is whichever date the API actually served.
		Returns { words: [], date: null } (and sets pyramid/loadError) on failure.
	 */
	async FETCH_PYRAMID_OF_DATE({ commit, rootGetters, rootState }, payload) {
		const isPreviousPuzzle = rootGetters["navigation/isFormerDatePirmyd"];
		const requestedOwnLocalDate = Boolean(payload && payload.date);
		let date = payload && payload.date;

		if (!date && isPreviousPuzzle) {
			date = rootState.navigation.currentRouteParams?.date;
		}

		const requestPuzzle = async (dateToRequest) => {
			const data = await this.$axios.$get(
				`${this.$config.API_SERVICE}/v4/pirmyd`,
				{ params: dateToRequest ? { date: dateToRequest } : {} }
			);

			return {
				rootWord: data.rootWord,
				puzzle: data.puzzle,
				resolvedDate: data.date,
				dayNumber: data.dayNumber,
			};
		};

		try {
			let result;

			try {
				result = await requestPuzzle(date);
			} catch (error) {
				const status = error.response && error.response.status;

				if (requestedOwnLocalDate && status === 400) {
					// Our local "today" guess isn't published yet on the server —
					// fall back to whatever the server's latest puzzle is.
					result = await requestPuzzle(undefined);
				} else {
					throw error;
				}
			}

			const { rootWord, puzzle, resolvedDate, dayNumber } = result;

			commit("updateLoadError", false);
			commit("updatePuzzleMeta", {
				date: resolvedDate,
				dayNumber,
				isFormerDate: isPreviousPuzzle,
			});

			return {
				words: [
					{ word: rootWord, pos: null, definition: null },
					...(puzzle || []).map((level) => ({
						word: level.word,
						pos: level.pos,
						definition: level.definition,
					})),
				],
				date: resolvedDate,
				dayNumber,
			};
		} catch (error) {
			console.error("Could not load the pirmyd puzzle", error);
			commit("updateLoadError", true);
			commit("updatePuzzleMeta", { date: null, dayNumber: null });
			return { words: [], date: null, dayNumber: null };
		}
	},
	SET_NEW_PYRAMID({ commit, state, dispatch, rootState }, pyramidToPlay) {
		// Nothing to set up if the puzzle failed to load (see FETCH_PYRAMID_OF_DATE).
		if (isEmpty(pyramidToPlay)) {
			return;
		}

		// A fresh puzzle load must never carry over a feedback message (or its
		// pending timeout) left behind by whatever puzzle this page is leaving —
		// otherwise a stale timer could fire level/CHANGE_LEVEL or clear tiles
		// against the puzzle that's about to be set up here (WFW-6683-style bug).
		dispatch("level/CLEAR_PENDING_FEEDBACK_TIMEOUT", null, { root: true });
		commit("level/updateFeedbackState", null, { root: true });

		// A fresh puzzle load (today's, or navigating to a different former
		// date) should never inherit the previous puzzle's finished state —
		// RESTORE_SAVED_PYRAMID_INFO (today only) will flip this back on
		// right after this if today's puzzle was already finished.
		commit("updatePyramidSolved", false);

		commit("input/resetTiles", {}, { root: true });

		// for each level of the pyramid, construct an array of tiles
		if (isEmpty(rootState.input.tiles)) {
			pyramidToPlay.forEach((element, elementIndex) => {
				// For the first level, fill the tiles with the letters of the first word of that level sorted alphabetically
				if (elementIndex === 0) {
					return commit(
						"input/setLevelsOfTiles",
						element.word.split("").sort().join(""),
						{ root: true }
					);
				}
				// The rest of the levels will be an empty placeholders for the moment
				return commit("input/setLevelsOfTiles", "", { root: true });
			});
		}

		commit("setPyramid", pyramidToPlay);

		const firstLevelOfPyramid = pyramidToPlay[0];
		commit(
			"level/setState",
			{
				lastLevelIndex: 0,
				lastLevelLength: firstLevelOfPyramid.word.length,
				levelSolution: pyramidToPlay[1],
			},
			{ root: true }
		);
	},
	RESTORE_SAVED_PYRAMID_INFO(
		{ commit, state, dispatch, rootState },
		pyramidToPlay
	) {
		// localStorage only exists in the browser. This action is only ever invoked
		// client-side (after hydration), but guard it anyway so it can never crash
		// the server if it's ever called from somewhere SSR-reachable in the future.
		if (!process.client) {
			return;
		}

		const currentLocalDate = new Date();
		const savedGameDate = parseInt(
			localStorage.getItem(LOCAL_STORAGE_KEYS.PRMD_DAY)
		);
		const savedGameMonth = parseInt(
			localStorage.getItem(LOCAL_STORAGE_KEYS.PRMD_MONTH)
		);

		// If the date and month coincide with today, then we can restore the last level info
		if (
			currentLocalDate.getMonth() + 1 === savedGameMonth &&
			currentLocalDate.getDate() === savedGameDate
		) {
			commit(
				"level/setState",
				{
					lastLevelIndex: parseInt(
						localStorage.getItem(
							LOCAL_STORAGE_KEYS.PRMD_LAST_LEVEL_INDEX
						)
					),
					lastLevelLength: parseInt(
						localStorage.getItem(
							LOCAL_STORAGE_KEYS.PRMD_LAST_LEVEL_LENGTH
						)
					),
					levelSolution:
						pyramidToPlay[
							parseInt(
								localStorage.getItem(
									LOCAL_STORAGE_KEYS.PRMD_LAST_LEVEL_INDEX
								)
							) + 1
						],
				},
				{ root: true }
			);
			commit(
				"input/updateTriedOutWords",
				JSON.parse(
					localStorage.getItem(LOCAL_STORAGE_KEYS.PRMD_TRIED_WORDS)
				),
				{ root: true }
			);
			commit(
				"input/updateTilesEntirely",
				JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.PRMD_TILES)),
				{ root: true }
			);
			commit(
				"updatePyramidSolved",
				JSON.parse(
					localStorage.getItem(LOCAL_STORAGE_KEYS.PRMD_SOLVED) ||
						"false"
				)
			);
			commit(
				"updateCluesSeenByLevel",
				JSON.parse(
					localStorage.getItem(
						LOCAL_STORAGE_KEYS.PRMD_CLUES_SEEN_BY_LEVEL
					) || "{}"
				)
			);
		} else {
			Object.keys(localStorage)
				.filter((key) => key.includes(LOCAL_STORAGE_KEYS.PRMD))
				.map((keyToRemove) => localStorage.removeItem(keyToRemove));
		}
	},
	RESTORE_SAVED_GENERIC_INFO({ commit, state, dispatch, rootState }) {
		if (!process.client) {
			return;
		}

		if (localStorage.getItem(LOCAL_STORAGE_KEYS.PRMD_SHOW_RULES) !== null) {
			commit(
				"updateShowRules",
				JSON.parse(
					localStorage.getItem(LOCAL_STORAGE_KEYS.PRMD_SHOW_RULES)
				)
			);
		} else {
			commit("updateShowRules", true);
		}

		if (localStorage.getItem(LOCAL_STORAGE_KEYS.PRMD_STATS) !== null) {
			commit(
				"stats/resetStats",
				JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.PRMD_STATS)),
				{ root: true }
			);
		}

		if (localStorage.getItem(LOCAL_STORAGE_KEYS.PRMD_SHOW_CLUES) !== null) {
			commit(
				"updateShowClues",
				JSON.parse(
					localStorage.getItem(LOCAL_STORAGE_KEYS.PRMD_SHOW_CLUES)
				)
			);
		}
	},
};
